import type { JSX } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Input } from '@/components';
import { Textarea } from '@/components/ui/textarea';
import { ClosedEnumSelect } from '@/features/catalogs/components/fieldPatternInputs';
import { CATALOG_IDS } from '@/data/models/catalog';
import { unitFieldTooltips } from '@/features/companies/components/masterDataFieldTooltips';
import type { RefrigerationUnitCoreFormValues } from '../config/core';
import { isFieldValueEmpty } from '@/data/fieldPatterns';
import { CatalogSuggestSelect } from '@/components/ui';
import { FormSection, FormGrid, FormField, FieldInfoButton } from '@/components/blocks/forms';

export function RefrigerationUnitGeneralInfoSection(): JSX.Element {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext<RefrigerationUnitCoreFormValues>();
  const labelWithHelp = (label: string, tooltip?: string) =>
    tooltip ? <FieldInfoButton label={label} content={tooltip} /> : label;

  return (
    <div className="space-y-4">
      <FormSection title="Informazioni generali">
        <FormGrid>
          <FormField
            label={labelWithHelp('Nome impianto', unitFieldTooltips.general.name)}
            htmlFor="refrigerationUnit-name"
            required
            error={errors.name?.message?.toString()}
          >
            <Input
              id="refrigerationUnit-name"
              placeholder="Es: Centrale frigorifera 2C"
              {...register('name', { required: 'Inserisci il nome' })}
            />
          </FormField>

          <FormField
            label={labelWithHelp('Ubicazione', unitFieldTooltips.general.location)}
            htmlFor="refrigerationUnit-location"
            required
            error={errors.location?.message?.toString()}
          >
            <Controller
              name="location"
              control={control}
              rules={{
                validate: (value) => (!isFieldValueEmpty(value) ? true : "Inserisci l'ubicazione"),
              }}
              render={({ field }) => (
                <CatalogSuggestSelect
                  id="refrigerationUnit-location"
                  catalogId={CATALOG_IDS.PLANT_LOCATIONS}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Es: Sala macchine 2C"
                  required
                  createIdPrefix="ubicazione"
                />
              )}
            />
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title="Parametri tecnici">
        <FormGrid>
          <FormField
            label={labelWithHelp('Gas tossico', unitFieldTooltips.general.toxicGasType)}
            htmlFor="refrigerationUnit-toxic-gas"
            required
            error={errors.toxicGasType?.message?.toString()}
          >
            <Controller
              name="toxicGasType"
              control={control}
              rules={{
                validate: (value) =>
                  !isFieldValueEmpty(value) ? true : 'Seleziona il gas tossico',
              }}
              render={({ field }) => (
                <ClosedEnumSelect
                  id="refrigerationUnit-toxic-gas"
                  catalogId={CATALOG_IDS.TOXIC_GASES}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Seleziona gas tossico"
                  required
                />
              )}
            />
          </FormField>

          <FormField
            label={labelWithHelp('Tipo impianto', unitFieldTooltips.general.impiantoType)}
            htmlFor="refrigerationUnit-type"
            required
            error={errors.impiantoType?.message?.toString()}
          >
            <Controller
              name="impiantoType"
              control={control}
              rules={{
                validate: (value) =>
                  !isFieldValueEmpty(value) ? true : 'Seleziona il tipo impianto',
              }}
              render={({ field }) => (
                <CatalogSuggestSelect
                  id="refrigerationUnit-type"
                  catalogId={CATALOG_IDS.PLANT_TYPES}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Seleziona tipo impianto"
                  required
                  createIdPrefix="impianto"
                />
              )}
            />
          </FormField>

          <FormField
            label={labelWithHelp('Carica NH₃ (kg)', unitFieldTooltips.general.ammoniaKg)}
            htmlFor="refrigerationUnit-ammonia"
            required
            error={errors.ammoniaKg?.message?.toString()}
          >
            <Input
              id="refrigerationUnit-ammonia"
              type="number"
              step="0.1"
              min={0}
              placeholder="Es: 2500"
              {...register('ammoniaKg', {
                valueAsNumber: true,
                required: 'Inserisci la carica NH₃',
                min: { value: 0, message: 'La carica deve essere >= 0' },
              })}
            />
          </FormField>

          <FormField
            label={labelWithHelp(
              'Manutentore incaricato',
              unitFieldTooltips.maintenance.maintenanceName
            )}
            htmlFor="maintenance-contractor"
            error={errors.maintenanceContractorName?.message?.toString()}
          >
            <Input
              id="maintenance-contractor"
              placeholder="Es: TecnoFrigo Service S.r.l."
              {...register('maintenanceContractorName')}
            />
          </FormField>
        </FormGrid>
      </FormSection>

      <FormSection title="Note">
        <FormGrid>
          <FormField
            label={labelWithHelp('Note aggiuntive', unitFieldTooltips.notes.notes)}
            htmlFor="refrigerationUnit-notes"
            colSpan={2}
            error={errors.notes?.message?.toString()}
          >
            <Textarea
              id="refrigerationUnit-notes"
              rows={4}
              placeholder="Note facoltative"
              {...register('notes')}
            />
          </FormField>
        </FormGrid>
      </FormSection>
    </div>
  );
}
